"use client";

import {
  type FormEvent,
  useEffect,
  useState,
} from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  browserPopupRedirectResolver,
  signOut,
  type User,
} from "firebase/auth";
import {
  auth,
  googleProvider,
} from "@/lib/firebase";
import { trackEvent } from "@/lib/analytics";

type AgentDetails = {
  id: string;
  name: string;
  status: string;
  balance: {
    complimentarySignalsRemaining:
      number;
    paidSignalsRemaining: number;
    totalSignalsRemaining: number;
  };
};

type AccessResponse = {
  registered: boolean;
  apiKey?: string;
  keyShownOnce?: boolean;
  agent?: AgentDetails;
  operator?: {
    email: string;
  };
  error?: string;
};

async function readResponse(
  response: Response,
) {
  const payload =
    await response.json() as AccessResponse;

  if (!response.ok) {
    throw new Error(
      payload.error ||
        "MarginGlow could not complete this request.",
    );
  }

  return payload;
}

export default function SignalAccessPage() {
  const [user, setUser] =
    useState<User | null>(null);

  const [authReady, setAuthReady] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [status, setStatus] =
    useState<AccessResponse | null>(
      null,
    );

  const [agentName, setAgentName] =
    useState("");

  const [acceptedTerms, setAcceptedTerms] =
    useState(false);

  const [issuedKey, setIssuedKey] =
    useState("");

  const [copied, setCopied] =
    useState(false);

  const [error, setError] =
    useState("");

  async function loadAccessStatus(
    currentUser: User,
  ) {
    setLoading(true);
    setError("");

    try {
      const idToken =
        await currentUser.getIdToken();

      const response = await fetch(
        "/api/agent/register",
        {
          headers: {
            Authorization:
              `Bearer ${idToken}`,
          },
          cache: "no-store",
        },
      );

      setStatus(
        await readResponse(response),
      );
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Agent access could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        (currentUser) => {
          setUser(currentUser);
          setAuthReady(true);
          setIssuedKey("");
          setCopied(false);

          if (currentUser) {
            void loadAccessStatus(
              currentUser,
            );
          } else {
            setStatus(null);
          }
        },
      );

    return unsubscribe;
  }, []);

  async function handleGoogleSignIn() {
    setLoading(true);
    setError("");

    try {
      const result =
        await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);

      if (!agentName) {
        const displayName =
          result.user.displayName
            ?.trim();

        setAgentName(
          displayName
            ? `${displayName}'s AI agent`
            : "My AI agent",
        );
      }

      trackEvent(
        "signal_agent_signin",
        {
          provider: "google",
          page_path:
            "/signal/access",
        },
      );
    } catch (signInError) {
      setError(
        signInError instanceof Error
          ? signInError.message
          : "Google sign-in could not be completed.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut(auth);
    setIssuedKey("");
    setStatus(null);
    setError("");
  }

  async function handleRegister(
    event: FormEvent,
  ) {
    event.preventDefault();

    if (!user) {
      setError(
        "Sign in before creating agent access.",
      );
      return;
    }

    setLoading(true);
    setError("");
    setIssuedKey("");
    setCopied(false);

    try {
      const idToken =
        await user.getIdToken();

      const response = await fetch(
        "/api/agent/register",
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Authorization:
              `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            agentName,
            acceptedTerms,
          }),
        },
      );

      const payload =
        await readResponse(response);

      setStatus(payload);
      setIssuedKey(
        payload.apiKey || "",
      );

      trackEvent(
        "signal_agent_registered",
        {
          page_path:
            "/signal/access",
          complimentary_signals: 1,
        },
      );
    } catch (registrationError) {
      setError(
        registrationError
          instanceof Error
          ? registrationError.message
          : "Agent access could not be created.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleRotate() {
    if (
      !user ||
      !window.confirm(
        "Rotate this key? The previous key will stop working immediately.",
      )
    ) {
      return;
    }

    setLoading(true);
    setError("");
    setIssuedKey("");
    setCopied(false);

    try {
      const idToken =
        await user.getIdToken();

      const response = await fetch(
        "/api/agent/register",
        {
          method: "PATCH",
          headers: {
            Authorization:
              `Bearer ${idToken}`,
          },
        },
      );

      const payload =
        await readResponse(response);

      setStatus(payload);
      setIssuedKey(
        payload.apiKey || "",
      );

      trackEvent(
        "signal_agent_key_rotated",
        {
          page_path:
            "/signal/access",
        },
      );
    } catch (rotationError) {
      setError(
        rotationError instanceof Error
          ? rotationError.message
          : "The agent key could not be rotated.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke() {
    if (
      !user ||
      !window.confirm(
        "Revoke this agent? Its API key will stop working immediately. Existing paid credits will be preserved.",
      )
    ) {
      return;
    }

    setLoading(true);
    setError("");
    setIssuedKey("");
    setCopied(false);

    try {
      const idToken =
        await user.getIdToken();

      const response = await fetch(
        "/api/agent/register",
        {
          method: "DELETE",
          headers: {
            Authorization:
              `Bearer ${idToken}`,
          },
        },
      );

      setStatus(
        await readResponse(response),
      );

      trackEvent(
        "signal_agent_revoked",
        {
          page_path:
            "/signal/access",
        },
      );
    } catch (revocationError) {
      setError(
        revocationError instanceof Error
          ? revocationError.message
          : "Agent access could not be revoked.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!issuedKey) {
      return;
    }

    await navigator.clipboard.writeText(
      issuedKey,
    );

    setCopied(true);
  }

  const agent = status?.agent;
  const registered =
    Boolean(
      status?.registered &&
      agent,
    );

  return (
    <main className="min-h-screen bg-[#fbf8f3] text-[#092723]">
      <header className="border-b border-[#d7e5df] px-6 py-5">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-6">
          <a
            href="/signal"
            className="text-2xl font-light tracking-tight"
          >
            MarginGlow
            <span className="text-[#ff5a3d]">
              .
            </span>
          </a>

          <a
            href="/signal"
            className="text-sm font-light text-[#007c7a]"
          >
            Back to AI Signal
          </a>
        </div>
      </header>

      <section className="px-6 py-14 md:py-20">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm font-light uppercase tracking-[0.22em] text-[#007c7a]">
            Authorised agent access
          </p>

          <h1 className="mt-4 max-w-3xl text-5xl font-light tracking-tight md:text-6xl">
            Connect your AI agent to
            MarginGlow Signal.
          </h1>

          <p className="mt-6 max-w-3xl text-lg font-light leading-8 text-[#52635f]">
            Create one secure agent
            credential and receive one
            complimentary business
            opportunity Signal. Additional
            Signals cost €2 through the
            agent purchasing tool.
          </p>

          <div className="mt-10 grid gap-8 lg:grid-cols-[0.7fr_1.3fr]">
            <aside className="rounded-[2rem] bg-[#092723] p-7 text-white">
              <p className="text-sm font-light uppercase tracking-[0.18em] text-[#9bcfc6]">
                What happens
              </p>

              <ol className="mt-6 space-y-5 text-sm font-light leading-6 text-[#d7e5df]">
                <li>
                  1. Sign in with a verified
                  Google account.
                </li>
                <li>
                  2. Name the AI agent or
                  integration you operate.
                </li>
                <li>
                  3. Copy the key when it is
                  shown. MarginGlow cannot
                  display it again.
                </li>
                <li>
                  4. Add the key as the
                  Authorization header in your
                  MCP client.
                </li>
              </ol>

              <div className="mt-8 rounded-2xl bg-white/10 p-4 text-xs font-light leading-6 text-[#d7e5df]">
                Endpoint:
                <span className="mt-1 block break-all font-mono text-white">
                  https://www.marginglow.com/mcp
                </span>
              </div>
            </aside>

            <section className="rounded-[2rem] border border-[#d7e5df] bg-white p-7 shadow-sm md:p-9">
              {!authReady ? (
                <p className="font-light text-[#52635f]">
                  Checking your secure
                  session…
                </p>
              ) : !user ? (
                <div>
                  <h2 className="text-3xl font-light tracking-tight">
                    Start with Google
                  </h2>

                  <p className="mt-4 font-light leading-7 text-[#52635f]">
                    A verified person must
                    remain responsible for
                    every agent credential.
                    MarginGlow never shares
                    the operator’s email with
                    the calling agent.
                  </p>

                  <button
                    type="button"
                    onClick={
                      handleGoogleSignIn
                    }
                    disabled={loading}
                    className="mt-7 rounded-full bg-[#007c7a] px-7 py-4 font-light text-white disabled:opacity-50"
                  >
                    {loading
                      ? "Connecting…"
                      : "Continue with Google"}
                  </button>
                </div>
              ) : loading && !status ? (
                <p className="font-light text-[#52635f]">
                  Loading agent access…
                </p>
              ) : !registered ? (
                <form
                  onSubmit={
                    handleRegister
                  }
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-3xl font-light tracking-tight">
                        Create agent access
                      </h2>
                      <p className="mt-2 text-sm font-light text-[#52635f]">
                        Signed in as{" "}
                        {user.email}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={
                        handleSignOut
                      }
                      className="text-sm font-light text-[#007c7a]"
                    >
                      Sign out
                    </button>
                  </div>

                  <label className="mt-7 block">
                    <span className="text-sm font-medium">
                      Agent or integration
                      name
                    </span>
                    <input
                      type="text"
                      value={agentName}
                      onChange={(event) => {
                        setAgentName(
                          event.target.value,
                        );
                      }}
                      minLength={2}
                      maxLength={100}
                      required
                      placeholder="Example: My business adviser agent"
                      className="mt-2 w-full rounded-2xl border border-[#b9cbc5] px-4 py-3 font-light outline-none focus:border-[#007c7a]"
                    />
                  </label>

                  <label className="mt-6 flex items-start gap-3 text-sm font-light leading-6 text-[#52635f]">
                    <input
                      type="checkbox"
                      checked={
                        acceptedTerms
                      }
                      onChange={(event) => {
                        setAcceptedTerms(
                          event.target.checked,
                        );
                      }}
                      required
                      className="mt-1"
                    />
                    <span>
                      I accept the{" "}
                      <a
                        href="/terms"
                        className="text-[#007c7a] underline"
                      >
                        terms
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        className="text-[#007c7a] underline"
                      >
                        privacy boundaries
                      </a>
                      , and I will protect the
                      issued credential.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !acceptedTerms
                    }
                    className="mt-7 rounded-full bg-[#ff5a3d] px-7 py-4 font-light text-white disabled:opacity-50"
                  >
                    {loading
                      ? "Creating access…"
                      : "Create agent and get one free Signal"}
                  </button>
                </form>
              ) : (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-light uppercase tracking-[0.16em] text-[#007c7a]">
                        Agent account
                      </p>
                      <h2 className="mt-2 text-3xl font-light tracking-tight">
                        {agent?.name}
                      </h2>
                    </div>

                    <button
                      type="button"
                      onClick={
                        handleSignOut
                      }
                      className="text-sm font-light text-[#007c7a]"
                    >
                      Sign out
                    </button>
                  </div>

                  <dl className="mt-7 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl bg-[#fbf8f3] p-4">
                      <dt className="text-xs font-light uppercase tracking-[0.14em] text-[#52635f]">
                        Status
                      </dt>
                      <dd className="mt-2 text-lg font-light capitalize">
                        {agent?.status}
                      </dd>
                    </div>

                    <div className="rounded-2xl bg-[#fbf8f3] p-4">
                      <dt className="text-xs font-light uppercase tracking-[0.14em] text-[#52635f]">
                        Signals remaining
                      </dt>
                      <dd className="mt-2 text-lg font-light">
                        {
                          agent?.balance
                            .totalSignalsRemaining
                        }
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-6 rounded-2xl border border-[#d7e5df] p-4">
                    <p className="text-xs font-light uppercase tracking-[0.14em] text-[#52635f]">
                      Agent ID
                    </p>
                    <p className="mt-2 break-all font-mono text-sm text-[#007c7a]">
                      {agent?.id}
                    </p>
                  </div>

                  {issuedKey ? (
                    <div className="mt-7 rounded-[1.5rem] border-2 border-[#ff5a3d] bg-[#fff5f1] p-5">
                      <h3 className="text-xl font-medium">
                        Copy this key now
                      </h3>

                      <p className="mt-2 text-sm font-light leading-6 text-[#52635f]">
                        This is the only time
                        MarginGlow will display
                        it. Store it in your
                        agent’s secret or
                        credential manager.
                      </p>

                      <textarea
                        readOnly
                        value={issuedKey}
                        rows={4}
                        className="mt-4 w-full resize-none rounded-xl border border-[#f2b2a4] bg-white p-3 font-mono text-xs"
                      />

                      <button
                        type="button"
                        onClick={
                          handleCopy
                        }
                        className="mt-3 rounded-full bg-[#ff5a3d] px-5 py-3 text-sm font-light text-white"
                      >
                        {copied
                          ? "Copied"
                          : "Copy API key"}
                      </button>
                    </div>
                  ) : (
                    <p className="mt-6 rounded-2xl bg-[#e4f4ee] p-4 text-sm font-light leading-6 text-[#52635f]">
                      Your key is securely
                      hashed and cannot be
                      displayed again. Rotate
                      it below if you no longer
                      have your original copy.
                    </p>
                  )}

                  <div className="mt-7 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={
                        handleRotate
                      }
                      disabled={loading}
                      className="rounded-full bg-[#007c7a] px-6 py-3 text-sm font-light text-white disabled:opacity-50"
                    >
                      {agent?.status ===
                      "revoked"
                        ? "Reactivate and issue new key"
                        : "Rotate API key"}
                    </button>

                    {agent?.status !==
                    "revoked" ? (
                      <button
                        type="button"
                        onClick={
                          handleRevoke
                        }
                        disabled={loading}
                        className="rounded-full border border-[#b9cbc5] px-6 py-3 text-sm font-light text-[#52635f] disabled:opacity-50"
                      >
                        Revoke access
                      </button>
                    ) : null}
                  </div>
                </div>
              )}

              {error ? (
                <p
                  role="alert"
                  className="mt-6 rounded-2xl border border-[#f2b2a4] bg-[#fff5f1] p-4 text-sm font-light leading-6 text-[#8f2f20]"
                >
                  {error}
                </p>
              ) : null}
            </section>
          </div>

          <p className="mt-10 text-center text-sm font-light text-[#52635f]">
            MarginGlow stores only a secure
            hash of the agent credential.
            Private customer data is never
            required for a Signal.
          </p>
        </div>
      </section>
    </main>
  );
}
